import {FC, useCallback, useMemo, useState} from 'react';
//mui
import {Box, MenuItem, Select as MuiSelect, SelectChangeEvent, FormControl, SxProps, Theme} from '@mui/material';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//icons
import {ReactComponent as AcceptIcon} from '@icons/accept.svg';
import {ReactComponent as ArrowDownIcon} from '@icons/select-arrow-down.svg';
//types
import {DefaultCheckboxColor, SelectOptionsType} from '@/utils/types';
//helpers
import {getValidationErrorsFromArray, getValidationErrorsFromObject} from '@/utils/helpers';
//components
import InputLabel from '../Inputs/InputLabel';
import FormHelperText from '../FormHelperText';
import Loader from '../Loader';
//styles
import {selectStyle} from './Select.styles';

interface SelectProps {
  value: string;
  options: SelectOptionsType[] | null;
  defaultValue?: string;
  sx?: SxProps<Theme>;
  field?: FieldInputProps<string | number>;
  form?: FormikProps<any>;
  label?: string;
  placeholder?: string;
  id?: number;
  name?: string;
  parentClassName?: SxProps<Theme>;
  disabled?: boolean;
  checkboxType?: DefaultCheckboxColor;
  disableErrorMessage?: boolean;
  customError?: string;
  isLoading?: boolean;
  isArrayData?: boolean;
  isObjectData?: boolean;
  onChange?: (event: SelectChangeEvent, option: SelectOptionsType, id?: number) => void;
}

const Select: FC<SelectProps> = ({
  value,
  options,
  field,
  form,
  label,
  defaultValue,
  id,
  disableErrorMessage,
  customError,
  parentClassName,
  checkboxType,
  placeholder,
  disabled,
  onChange,
  isLoading,
  isArrayData,
  isObjectData,
  ...rest
}) => {
  const [isSelectPopoverOpen, setIsSelectPopoverOpen] = useState(false);

  const errorText = useMemo(() => {
    if (field && form) {
      if (isArrayData) return getValidationErrorsFromArray(field, form);
      if (isObjectData) return getValidationErrorsFromObject(field, form);
      return (form.errors[field.name] && form.touched[field.name] && (form.errors[field.name] as string)) || undefined;
    }
  }, [field, form]);

  const changeHandler = (event: SelectChangeEvent) => {
    const {value} = event.target;

    if (onChange) {
      const targetOption = options?.find((option) => option.id === value) as SelectOptionsType;
      onChange(event, targetOption, id);
    } else if (form && field) {
      form.setFieldValue(field.name, value);
    }
  };

  const selectOptions = options?.map((item: SelectOptionsType) => {
    return (
      <MenuItem key={item.id} value={item.id} sx={selectStyle.optionItem}>
        {item.name}
        {value === item.id && <AcceptIcon />}
      </MenuItem>
    );
  });

  //   const handlePopoverChange = () => setIsSelectPopoverOpen(!isSelectPopoverOpen);
  const openPopover = () => setIsSelectPopoverOpen(true);
  const closePopover = () => setIsSelectPopoverOpen(false);

  const iconArrowDown = useCallback(
    () => (
      <Box
        onClick={() => !disabled && openPopover()}
        sx={{
          ...selectStyle.icon,
          ...(!!errorText && selectStyle.iconError),
          ...(disabled && selectStyle.iconDisabled),
          ...(isSelectPopoverOpen && selectStyle.iconRotate),
          cursor: disabled ? 'default' : 'pointer',
          pointerEvents: 'auto',
        }}
      >
        {isLoading ? <Loader /> : <ArrowDownIcon />}
      </Box>
    ),
    [errorText, disabled, isSelectPopoverOpen, isLoading]
  );

  return (
    <Box sx={{...parentClassName, ...selectStyle.wrapper}}>
      {label && <InputLabel label={label} error={!!errorText} disabled={disabled} />}

      <FormControl disabled={disabled} error={!!errorText}>
        <MuiSelect
          {...rest}
          value={value || ''}
          defaultValue={defaultValue}
          open={isSelectPopoverOpen}
          onOpen={openPopover}
          onClose={closePopover}
          // onOpen={handlePopoverChange}
          // onClose={handlePopoverChange}
          label={false}
          onChange={changeHandler}
          displayEmpty
          IconComponent={iconArrowDown}
          renderValue={(selected) => {
            if (selected) {
              return options?.find((item: SelectOptionsType) => item.id === selected)?.name;
            }
            if (placeholder) {
              return <Box sx={{...(!errorText && selectStyle.placeholder)}}>{placeholder}</Box>;
            }

            return defaultValue;
          }}
        >
          {selectOptions}
        </MuiSelect>
      </FormControl>

      {!disableErrorMessage && errorText && <FormHelperText error title={errorText} />}
    </Box>
  );
};

export default Select;
